import React, { useState, useEffect } from "react";

// Shared, read-only renderer for a single funnel block. Used by both the builder
// preview and the public (unauthenticated) page renderer.
export default function BlockRenderer({ block, brand = "#6366F1", onSubmit, submitting }) {
  const p = block.props || {};
  const alignClass = { left: "text-left items-start", center: "text-center items-center", right: "text-right items-end" }[p.align || "center"];

  switch (block.type) {
    case "heading": {
      const size = { sm: "text-2xl", md: "text-3xl", lg: "text-4xl", xl: "text-5xl sm:text-6xl" }[p.size || "xl"];
      return <h1 className={`font-display font-extrabold tracking-tight ${size}`} style={{ textAlign: p.align || "center" }}>{p.text || "Headline"}</h1>;
    }
    case "text":
      return <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-2xl mx-auto" style={{ textAlign: p.align || "center" }}>{p.text || "Text"}</p>;
    case "image":
      return (
        <div className="flex" style={{ justifyContent: p.align === "left" ? "flex-start" : p.align === "right" ? "flex-end" : "center" }}>
          {p.url ? <img src={p.url} alt={p.alt || ""} className="rounded-xl max-w-full" style={{ width: p.width ? `${p.width}px` : "100%", maxWidth: "100%" }} />
            : <div className="w-full h-48 rounded-xl bg-white/5 border border-dashed border-white/20 flex items-center justify-center text-white/40 text-sm">Image</div>}
        </div>
      );
    case "video": {
      const embed = toEmbed(p.url);
      return embed ? (
        <div className="relative w-full max-w-3xl mx-auto rounded-xl overflow-hidden" style={{ aspectRatio: "16/9" }}>
          <iframe src={embed} title="video" className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
        </div>
      ) : <div className="w-full max-w-3xl mx-auto h-64 rounded-xl bg-white/5 border border-dashed border-white/20 flex items-center justify-center text-white/40 text-sm">Add a video URL</div>;
    }
    case "button": {
      const size = { sm: "h-9 px-4 text-sm", md: "h-11 px-6", lg: "h-14 px-8 text-lg" }[p.size || "lg"];
      return (
        <div className="flex" style={{ justifyContent: p.align === "left" ? "flex-start" : p.align === "right" ? "flex-end" : "center" }}>
          <button onClick={() => p.action === "submit" && onSubmit?.({}, block)}
            className={`rounded-full font-semibold text-white inline-flex items-center transition-transform hover:scale-[1.03] ${size}`}
            style={{ background: brand }}>{p.label || "Button"}</button>
        </div>
      );
    }
    case "countdown":
      return <Countdown minutes={p.minutes || 15} label={p.label} brand={brand} />;
    case "divider":
      return <hr className="border-white/10" />;
    case "spacer":
      return <div style={{ height: `${p.height || 40}px` }} />;
    case "form":
      return <FormBlock block={block} brand={brand} onSubmit={onSubmit} submitting={submitting} align={alignClass} />;
    default:
      return null;
  }
}

function toEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}

function Countdown({ minutes, label, brand }) {
  const [left, setLeft] = useState(minutes * 60);
  useEffect(() => {
    const t = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <div className="text-center">
      {label && <p className="text-sm uppercase tracking-widest text-white/60 mb-2">{label}</p>}
      <div className="inline-flex gap-2 font-mono text-4xl font-bold" style={{ color: brand }}>
        <span className="bg-white/5 rounded-lg px-3 py-2">{mm}</span>
        <span className="py-2">:</span>
        <span className="bg-white/5 rounded-lg px-3 py-2">{ss}</span>
      </div>
    </div>
  );
}

function FormBlock({ block, brand, onSubmit, submitting, align }) {
  const p = block.props || {};
  const fields = p.fields || [{ key: "name", label: "Name", type: "text", required: true }, { key: "email", label: "Email", type: "email", required: true }];
  const [values, setValues] = useState({});
  const submit = (e) => { e.preventDefault(); onSubmit?.(values, block); };
  return (
    <form onSubmit={submit} className="w-full max-w-md mx-auto space-y-3 bg-white/5 border border-white/10 rounded-2xl p-6">
      {p.title && <p className="font-display font-bold text-lg text-center mb-1">{p.title}</p>}
      {fields.map((f) => (
        <input key={f.key} type={f.type || "text"} required={f.required} placeholder={f.label}
          value={values[f.key] || ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
          data-testid={`funnel-form-${f.key}`}
          className="w-full h-11 px-4 rounded-lg bg-black/30 border border-white/15 text-white placeholder:text-white/40 outline-none focus:border-white/40" />
      ))}
      <button type="submit" disabled={submitting} data-testid="funnel-form-submit"
        className="w-full h-12 rounded-full font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
        style={{ background: brand }}>{submitting ? "Submitting…" : (p.submit_label || "Submit")}</button>
    </form>
  );
}
